import { createContext, useContext, useEffect, useState } from "react";
import type { CurrentUser } from "./types";
import { authEnabled, getProfile, getPhotoObjectUrl } from "./lib/auth";

// Modul-cache av innlogget bruker-id, så skrivekall kan sende X-User-Id uten å
// tråkle hooken gjennom alt. Byttes til JWT Object ID når Entra ID kobles på.
let currentUserId: string | null = null;
export function getCurrentUserId(): string | null {
  return currentUserId;
}

type CtxValue =
  | { kind: "loading" }
  | { kind: "ready"; user: CurrentUser }
  | { kind: "error"; message: string };

const CurrentUserContext = createContext<CtxValue>({ kind: "loading" });

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CtxValue>({ kind: "loading" });
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // I prod (SSO på) er den faktiske MS-kontoen fasit for navn/e-post.
    const profile = authEnabled ? getProfile() : null;

    fetch("/api/me")
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return (await res.json()) as CurrentUser;
      })
      .then((user) => {
        const merged: CurrentUser = profile
          ? { ...user, displayName: profile.name, email: profile.email }
          : user;
        currentUserId = merged.userId;
        if (!cancelled) setState({ kind: "ready", user: merged });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      });

    // Profilbilde fra Microsoft Graph (ikke-blokkerende, faller til initialer).
    if (authEnabled) {
      void getPhotoObjectUrl().then((url) => {
        if (url && !cancelled) setAvatarUrl(url);
      });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const value: CtxValue =
    state.kind === "ready"
      ? { kind: "ready", user: { ...state.user, avatarUrl } }
      : state;

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}

export function useCurrentUser(): CurrentUser | null {
  const ctx = useContext(CurrentUserContext);
  return ctx.kind === "ready" ? ctx.user : null;
}
